import * as React from "react";
import { StarField } from "@/components/StarField";
import { useInView } from "@/hooks/useInView";

/**
 * "Weitere Leistungen" – drei Marken-Icons rotieren auf einer liegenden Ellipse.
 * Die Position wird per requestAnimationFrame berechnet (kein CSS-3D nötig):
 * vorne = größer, heller, oben liegend = kleiner, dezenter. Dadurch überdecken
 * sich die Icons nie und die Tiefenwirkung bleibt sauber.
 */

const SERVICES = [
  {
    id: "google-ads",
    label: "Google Ads",
    description:
      "Sichtbar genau dann, wenn nach deiner Leistung gesucht wird – sauber aufgesetzt und laufend optimiert.",
    image: "/icon-google-ads.png",
    hue: 222,
    sizeRatio: 0.27,
  },
  {
    id: "seo",
    label: "SEO",
    description:
      "Nachhaltige Rankings statt kurzfristiger Strohfeuer: Technik, Inhalte und Struktur im Zusammenspiel.",
    image: "/icon-seo.png",
    hue: 262,
    sizeRatio: 0.25,
  },
  {
    id: "meta-ads",
    label: "Meta Ads",
    description:
      "Reichweite auf Instagram und Facebook – zielgenau ausgesteuert und ehrlich ausgewertet.",
    image: "/icon-meta.png",
    hue: 196,
    sizeRatio: 0.33,
  },
];

const ROTATION_SECONDS = 34; // eine volle Runde
const RADIUS_X = 33; // % der Stage-Breite
const RADIUS_Y = 13; // % der Stage-Höhe (flache Ellipse = Perspektive)

const useRotation = (paused) => {
  const [angle, setAngle] = React.useState(0);
  const frame = React.useRef();
  const last = React.useRef();

  React.useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }

    const tick = (time) => {
      if (last.current === undefined) last.current = time;
      const delta = (time - last.current) / 1000;
      last.current = time;
      if (!paused) {
        setAngle((prev) => (prev + (360 / ROTATION_SECONDS) * delta) % 360);
      }
      frame.current = window.requestAnimationFrame(tick);
    };

    frame.current = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frame.current);
      last.current = undefined;
    };
  }, [paused]);

  return angle;
};

const OrbitStage = ({ active, onActiveChange }) => {
  const angle = useRotation(Boolean(active));
  const stageRef = React.useRef(null);
  const [stageWidth, setStageWidth] = React.useState(660);

  React.useEffect(() => {
    const node = stageRef.current;
    if (!node) return undefined;
    const update = () => setStageWidth(node.offsetWidth || 660);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return (
    <div
      ref={stageRef}
      className="relative mx-auto aspect-[5/4] w-full max-w-[660px]"
    >
      {/* Zentrales Leuchten */}
      <div
        aria-hidden
        className="absolute left-1/2 top-1/2 h-[52%] w-[62%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,hsl(222_100%_58%/0.20),transparent)] blur-2xl motion-safe:animate-core-pulse"
      />

      {/* Bahnlinie */}
      <svg
        aria-hidden
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full"
      >
        <ellipse
          cx="50"
          cy="50"
          rx={RADIUS_X}
          ry={RADIUS_Y}
          fill="none"
          stroke="hsl(0 0% 100% / 0.14)"
          strokeWidth="1"
          strokeDasharray="6 9"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {SERVICES.map((service, index) => {
        const theta = ((angle + index * 120) * Math.PI) / 180;
        const depth = Math.cos(theta); // 1 = vorne, -1 = hinten
        const normalized = (depth + 1) / 2;
        const isActive = active === service.id;

        const left = 50 + RADIUS_X * Math.sin(theta);
        const top = 50 + RADIUS_Y * depth;
        const scale = (0.72 + 0.36 * normalized) * (isActive ? 1.12 : 1);
        const opacity = 0.5 + 0.5 * normalized;

        return (
          <button
            key={service.id}
            type="button"
            onMouseEnter={() => onActiveChange(service.id)}
            onMouseLeave={() => onActiveChange(null)}
            onFocus={() => onActiveChange(service.id)}
            onBlur={() => onActiveChange(null)}
            aria-label={service.label}
            className="absolute flex flex-col items-center gap-4 outline-none"
            style={{
              left: `${left}%`,
              top: `${top}%`,
              transform: `translate(-50%, -50%) scale(${scale})`,
              zIndex: Math.round(100 + depth * 50),
              opacity,
              transition: "opacity 300ms ease-out",
            }}
          >
            <span className="relative flex items-center justify-center">
              <span
                aria-hidden
                className="absolute h-[9rem] w-[9rem] rounded-full blur-2xl transition-opacity duration-500 sm:h-40 sm:w-40"
                style={{
                  background: `radial-gradient(closest-side, hsl(${service.hue} 100% 62% / ${
                    isActive ? 0.5 : 0.28
                  }), transparent)`,
                }}
              />
              <img
                src={service.image}
                alt={service.label}
                className="relative motion-safe:animate-float"
                style={{
                  width: `${Math.round(
                    stageWidth * service.sizeRatio * (stageWidth < 430 ? 0.78 : 1)
                  )}px`,
                  animationDuration: `${7 + index}s`,
                  animationDelay: `-${index * 2}s`,
                  filter: `drop-shadow(0 12px 30px hsl(${service.hue} 100% 55% / 0.35))`,
                }}
              />
            </span>

            <span
              className={`whitespace-nowrap rounded-full border px-4 py-1.5 font-display text-[11px] font-light uppercase tracking-[0.22em] backdrop-blur-md transition-colors duration-500 sm:text-xs ${
                isActive
                  ? "border-primary/60 bg-background/85 text-foreground"
                  : "border-border/70 bg-background/60 text-muted-foreground"
              }`}
            >
              {service.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export const ServicesOrbit = () => {
  const [active, setActive] = React.useState(null);
  const [ref, inView] = useInView({ threshold: 0.12 });

  return (
    <section
      id="leistungen"
      ref={ref}
      className="grain relative isolate overflow-hidden border-t border-border/60 bg-[hsl(0_0%_0%)] py-24 sm:py-28 lg:py-36"
    >
      <StarField count={55} className="-z-10 opacity-60" />
      <div
        aria-hidden
        className="absolute right-[-10%] top-1/2 -z-10 h-[720px] w-[720px] -translate-y-1/2 rounded-full bg-[radial-gradient(circle,hsl(222_100%_55%/0.14)_0%,transparent_65%)] blur-2xl"
      />

      <div className="mx-auto grid max-w-[1500px] grid-cols-1 items-center gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:px-12">
        {/* Text */}
        <div
          className={`transition-[opacity,transform] duration-700 ease-out lg:col-span-5 ${
            inView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <p className="font-display text-[11px] font-light uppercase tracking-[0.32em] text-primary-glow">
            Weitere Leistungen
          </p>

          <h2 className="mt-6 font-display text-3xl font-light leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Sichtbarkeit, die
            <br className="hidden sm:block" /> um deine Marke kreist.
          </h2>

          <div className="mt-7 h-px w-32 bg-gradient-hairline" aria-hidden />

          <p className="mt-7 max-w-lg text-sm font-light leading-relaxed text-muted-foreground sm:text-base">
            Zusätzlich zum Website-Abo: drei Disziplinen, die dich sichtbar
            machen – einzeln buchbar, im Zusammenspiel am stärksten.
          </p>

          <ul className="mt-10 flex flex-col divide-y divide-border/60 border-y border-border/60">
            {SERVICES.map((service) => {
              const isActive = active === service.id;
              return (
                <li key={service.id}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(service.id)}
                    onMouseLeave={() => setActive(null)}
                    onFocus={() => setActive(service.id)}
                    onBlur={() => setActive(null)}
                    className="group flex w-full items-start gap-5 py-5 text-left outline-none"
                  >
                    <span
                      aria-hidden
                      className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full transition-transform duration-500"
                      style={{
                        background: `radial-gradient(circle at 30% 30%, hsl(${service.hue} 100% 78%), hsl(${service.hue} 90% 45%))`,
                        boxShadow: `0 0 14px hsl(${service.hue} 95% 60% / ${
                          isActive ? 0.9 : 0.35
                        })`,
                        transform: isActive ? "scale(1.5)" : "scale(1)",
                      }}
                    />
                    <span className="flex min-w-0 flex-col">
                      <span
                        className={`font-display text-lg font-light tracking-wide transition-colors duration-300 sm:text-xl ${
                          isActive ? "text-foreground" : "text-foreground/80"
                        }`}
                      >
                        {service.label}
                      </span>
                      <span className="mt-1 text-xs font-light leading-relaxed text-muted-foreground sm:text-sm">
                        {service.description}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Rotierende Icons */}
        <div className="lg:col-span-7">
          <OrbitStage active={active} onActiveChange={setActive} />
        </div>
      </div>
    </section>
  );
};
